'use client'

import {useFloatingPosition} from '@fitro/overlay-layout'
import React, {useCallback, useEffect, useId, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {useDialogFocusTrap, isTopmostModal, markModalEscapeHandled, MODAL_ESCAPE_HANDLED, type ModalKeyboardEvent} from '@fitro/modal-routing'
import {cn} from '../../../lib/utils'
import {ActionItem, ActionSheet} from '../ActionSheet/ActionSheet'
import {usePortalContainer} from '../../../lib/portal-root'
import {useScrollLock} from '../../../hooks/useScrollLock'
import {getPopoverOriginClass, POPOVER_MOTION_CLASS} from '@fitro/customer-ui/popoverMotion'
import {useRetainedMotionState} from '@fitro/customer-ui/useRetainedMotionState'
import {CUSTOMER_OVERLAY_LAYERS} from '../../../lib/overlay-layers'

type DropdownPlacement =
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'left'
    | 'left-start'
    | 'left-end'
    | 'right'
    | 'right-start'
    | 'right-end'

type DropdownAlign = 'start' | 'center' | 'end'
type DropdownLayer = 'default' | 'selection'

type DropdownTriggerProps =
    React.HTMLAttributes<HTMLElement> &
    React.RefAttributes<HTMLElement>

interface DropdownBaseProps {
    trigger: React.ReactElement<DropdownTriggerProps>
    children: React.ReactNode
    placement?: DropdownPlacement
    align?: DropdownAlign
    sideOffset?: number
    className?: string
    matchTriggerWidth?: boolean
    open?: boolean
    onOpenChange?: (open: boolean) => void
    closeOnClickOutside?: boolean
    closeOnEscape?: boolean
    contentRole?: 'menu' | 'dialog'
    contentId?: string
    layer?: DropdownLayer
}

type DropdownPresentationProps =
    | {modal: true; contentAriaLabel: string; cancelText: string}
    | {modal?: false; contentAriaLabel?: string; cancelText?: never}

type DropdownProps = DropdownBaseProps & DropdownPresentationProps

interface DropdownItemProps {
    label: string
    icon?: React.ComponentType<{ className?: string }>
    onClick?: () => void
    variant?: 'default' | 'danger'
    disabled?: boolean
    className?: string
}

export const Dropdown: React.FC<DropdownProps> = ({
                                                      trigger,
                                                      children,
                                                      placement = 'bottom-start',
                                                      align: _align = 'start',
                                                      sideOffset = 4,
                                                      className,
                                                      matchTriggerWidth = false,
                                                      open: controlledOpen,
                                                      onOpenChange,
                                                      modal,
                                                      closeOnClickOutside = true,
                                                      closeOnEscape = true,
                                                      contentRole = 'menu',
                                                      contentAriaLabel,
                                                      cancelText,
                                                      contentId,
                                                      layer = 'default',
                                                  }) => {
    const [internalOpen, setInternalOpen] = useState(false)
    const generatedContentId = useId()
    const resolvedContentId = contentId ?? generatedContentId
    const isSelectionLayer = layer === 'selection'
    const dropdownZIndex = isSelectionLayer
        ? CUSTOMER_OVERLAY_LAYERS.selectionPanel
        : CUSTOMER_OVERLAY_LAYERS.dropdown
    const sheetBackdropZIndex = isSelectionLayer
        ? CUSTOMER_OVERLAY_LAYERS.selectionBackdrop
        : CUSTOMER_OVERLAY_LAYERS.actionSheetBackdrop
    const sheetPanelZIndex = isSelectionLayer
        ? CUSTOMER_OVERLAY_LAYERS.selectionPanel
        : CUSTOMER_OVERLAY_LAYERS.fullscreenEditor
    const triggerRef = useRef<HTMLElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const portalContainer = usePortalContainer()
    const isControlled = controlledOpen !== undefined

    const isOpen = isControlled ? controlledOpen : internalOpen
    const previousOpenRef = useRef(isOpen)
    const setIsOpen = useCallback((open: boolean) => {
        if (!isControlled) setInternalOpen(open)
        onOpenChange?.(open)
    }, [isControlled, onOpenChange])

    useEffect(() => {
        if (
            previousOpenRef.current
            && !isOpen
            && dropdownRef.current?.contains(document.activeElement)
        ) {
            triggerRef.current?.focus()
        }
        previousOpenRef.current = isOpen
    }, [isOpen])

    const [isMobile, setIsMobile] = useState(false)
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 640)
        checkMobile()
        window.addEventListener('resize', checkMobile)
        return () => window.removeEventListener('resize', checkMobile)
    }, [])

    const shouldUseModal = Boolean(modal && isMobile)
    useDialogFocusTrap(isOpen && !shouldUseModal && contentRole === 'dialog', dropdownRef, {tabNavigation: 'all-controls'})
    const {shouldRender, motionState} = useRetainedMotionState(isOpen && !shouldUseModal, 150)

    const {position, isPositioned, resolvedPlacement} = useFloatingPosition(
        triggerRef,
        dropdownRef,
        placement,
        sideOffset,
        matchTriggerWidth,
        shouldRender && !shouldUseModal,
        isOpen && !shouldUseModal,
        dropdownZIndex,
    )



    useScrollLock({
        enabled: shouldUseModal && isOpen,
    })

    useEffect(() => {
        if (!isOpen || !closeOnEscape) return

        const handleEscape = (event: KeyboardEvent) => {
            const panel = shouldUseModal ? document.getElementById(resolvedContentId) : dropdownRef.current
            if (event.key === 'Escape' && !(event as ModalKeyboardEvent)[MODAL_ESCAPE_HANDLED]
                && isTopmostModal(panel) && markModalEscapeHandled(event)) {
                event.preventDefault()
                setIsOpen(false)
                triggerRef.current?.focus()
            }
        }

        document.addEventListener('keydown', handleEscape, true)
        return () => document.removeEventListener('keydown', handleEscape, true)
    }, [isOpen, closeOnEscape, setIsOpen, shouldUseModal, resolvedContentId])

    useEffect(() => {
        if (!isOpen || !closeOnClickOutside || shouldUseModal) return

        const handleClickOutside = (event: PointerEvent) => {
            const target = event.target as Node
            if (
                !dropdownRef.current?.contains(target) &&
                !triggerRef.current?.contains(target)
            ) {
                setIsOpen(false)
            }
        }

        document.addEventListener('pointerdown', handleClickOutside)
        return () => document.removeEventListener('pointerdown', handleClickOutside)
    }, [isOpen, closeOnClickOutside, shouldUseModal, setIsOpen])

    useEffect(() => {
        if (
            !isOpen
            || shouldUseModal
            || !shouldRender
            || !isPositioned
            || contentRole !== 'dialog'
        ) {
            return
        }

        const animationFrameId = window.requestAnimationFrame(() => {
            dropdownRef.current
                ?.querySelector<HTMLElement>(
                    'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"]):not([disabled])'
                )
                ?.focus()
        })
        return () => window.cancelAnimationFrame(animationFrameId)
    }, [contentRole, isOpen, isPositioned, shouldRender, shouldUseModal])

    useEffect(() => {
        if (!isOpen || shouldUseModal || contentRole !== 'menu') return

        const handleKeyDown = (event: KeyboardEvent) => {
            if (!dropdownRef.current || !isTopmostModal(dropdownRef.current)) return
            const target = event.target instanceof Element ? event.target : document.activeElement
            if (!target || (!dropdownRef.current.contains(target) && !triggerRef.current?.contains(target))) return
            if (target.closest('input, textarea, select, [contenteditable="true"]')
                && event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Tab') return

            const focusableElements = dropdownRef.current.querySelectorAll(
                '[role="menuitem"]:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
            ) as NodeListOf<HTMLElement>

            if (focusableElements.length === 0) return

            const currentIndex = Array.from(focusableElements).findIndex(
                el => el === document.activeElement
            )

            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault()
                    const nextIndex = currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0
                    focusableElements[nextIndex].focus()
                    break
                case 'ArrowUp':
                    event.preventDefault()
                    const prevIndex = currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1
                    focusableElements[prevIndex].focus()
                    break
                case 'Home':
                    event.preventDefault()
                    focusableElements[0].focus()
                    break
                case 'End':
                    event.preventDefault()
                    focusableElements[focusableElements.length - 1].focus()
                    break
                case 'Tab':
                    setIsOpen(false)
                    break
            }
        }

        document.addEventListener('keydown', handleKeyDown, true)
        return () => document.removeEventListener('keydown', handleKeyDown, true)
    }, [contentRole, isOpen, shouldUseModal, setIsOpen])

    const triggerElement = React.cloneElement(trigger, {
        ref: triggerRef,
        onClick: (e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault()
            e.stopPropagation()
            setIsOpen(!isOpen)
            trigger.props.onClick?.(e)
        },
        'aria-expanded': isOpen,
        'aria-haspopup': contentRole,
        'aria-controls': isOpen ? resolvedContentId : undefined,
    })

    const renderMobileContent = () => {
        const items = React.Children.toArray(children)
        return items.map((child, index) => {
            if (React.isValidElement(child) && child.type === DropdownItem) {
                const props = child.props as DropdownItemProps
                return (
                    <ActionItem
                        key={index}
                        label={props.label}
                        icon={props.icon}
                        onClick={() => {
                            props.onClick?.()
                            setIsOpen(false)
                        }}
                        variant={props.variant === 'danger' ? 'destructive' : 'default'}
                        disabled={props.disabled}
                    />
                )
            }
            return child
        })
    }

    if (modal && isMobile) {
        return (
            <>
                {triggerElement}
                <ActionSheet
                    open={isOpen}
                    onClose={() => setIsOpen(false)}
                    title={contentAriaLabel}
                    cancelText={cancelText}
                    showCancel={true}
                    panelId={resolvedContentId}
                    returnFocusRef={triggerRef}
                    backdropZIndex={sheetBackdropZIndex}
                    panelZIndex={sheetPanelZIndex}
                    selectionBoundary={isSelectionLayer}
                >
                    {renderMobileContent()}
                </ActionSheet>
            </>
        )
    }

    const dropdownContent = shouldRender && (
        <div
            ref={dropdownRef}
            id={resolvedContentId}
            style={position}
            data-state={motionState}
            className={cn(
                'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700',
                'rounded-lg shadow-lg min-w-[160px] max-w-[320px]',
                'py-1 z-50',
                POPOVER_MOTION_CLASS,
                getPopoverOriginClass(resolvedPlacement),
                className
            )}
            role={contentRole}
            data-modal-root
            tabIndex={-1}
            aria-label={contentAriaLabel}
            data-fitro-selection-overlay={isSelectionLayer ? 'true' : undefined}
            aria-orientation={contentRole === 'menu' ? 'vertical' : undefined}
            aria-hidden={!isOpen || !isPositioned}
            inert={!isOpen || !isPositioned ? true : undefined}
            onKeyDown={(e) => {
                if (contentRole !== 'dialog') e.stopPropagation()
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onClickCapture={(e) => {
                // A closing menu stays mounted for its exit; a trailing compatibility click must not pick again.
                if (!isOpen) {
                    e.preventDefault()
                    e.stopPropagation()
                }
            }}
        >
            {children}
        </div>
    )

    return (
        <>
            {triggerElement}
            {typeof window !== 'undefined' && portalContainer && dropdownContent && createPortal(dropdownContent, portalContainer)}
        </>
    )
}

/**
 * Semantic dropdown for choosing a value. It owns the elevated overlay layer and
 * selection interaction boundary needed by portaled controls inside editors.
 */
export const SelectionDropdown: React.FC<Omit<DropdownBaseProps, 'layer'> & DropdownPresentationProps> = (props) => (
    <Dropdown {...props} layer="selection"/>
)

SelectionDropdown.displayName = 'SelectionDropdown'

export const DropdownItem: React.FC<DropdownItemProps> = ({
                                                              label,
                                                              icon: Icon,
                                                              onClick,
                                                              variant = 'default',
                                                              disabled = false,
                                                              className,
                                                          }) => {
    const variantClasses = {
        default: 'text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800',
        danger: 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
    }

    return (
        <button
            role="menuitem"
            onClick={(e) => {
                e.stopPropagation()
                if (!disabled) {
                    onClick?.()
                }
            }}
            disabled={disabled}
            className={cn(
                'w-full flex items-center gap-3 px-3 py-2',
                'text-left text-sm font-medium rounded-md',
                'transition-colors duration-200',
                'focus:outline-hidden focus:ring-2 focus:ring-primary/50 focus:ring-inset',
                'min-h-[44px] sm:min-h-[36px]',
                variantClasses[variant],
                disabled && 'opacity-50 cursor-not-allowed',
                className
            )}
        >
            {Icon && (
                <Icon className="w-4 h-4 shrink-0"/>
            )}
            <span className="flex-1 truncate">
        {label}
      </span>
        </button>
    )
}

const DropdownSeparator: React.FC<{ className?: string }> = ({className}) => (
    <div
        className={cn(
            'h-px bg-gray-200 dark:bg-gray-700 mx-1 my-1',
            className
        )}
        role="separator"
    />
)

Dropdown.displayName = 'Dropdown'
DropdownItem.displayName = 'DropdownItem'
DropdownSeparator.displayName = 'DropdownSeparator'
